import { useForm } from '@tanstack/react-form';
import type { InterviewTypeListItemDto } from '@sara/api-client';
import { ChevronDown, Link2, Phone } from 'lucide-react';
import { cn } from '@sara/ui/lib/utils';
import { Badge } from '@sara/ui/components/badge';
import { Button } from '@sara/ui/components/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@sara/ui/components/collapsible';
import { Input } from '@sara/ui/components/input';
import { Field, FieldContent, FieldError, FieldLabel } from '@sara/ui/components/field';
import { RadioGroup, RadioGroupItem } from '@sara/ui/components/radio-group';
import { PhoneInput } from '../PhoneInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sara/ui/components/select';

import { createInterviewFormSchema, type CreateInterviewFormValues } from './schemas';

interface CreateInterviewFormProps {
  interviewTypes: InterviewTypeListItemDto[];
  onSubmit: (values: CreateInterviewFormValues) => void;
  onCancel: () => void;
  isPending: boolean;
  lockedCandidate?: { id: string; firstName: string; lastName: string; email: string };
}

// An interview type can only run — as a call or a browser interview — once an
// ElevenLabs agent is linked. Without one the backend returns a 503, so we surface
// readiness up front. The list payload already includes `elevenlabsAgentId`.
function isTemplateReady(type: InterviewTypeListItemDto): boolean {
  return Boolean(type.elevenlabsAgentId);
}

function CreateInterviewForm({
  interviewTypes,
  onSubmit,
  onCancel,
  isPending,
  lockedCandidate,
}: CreateInterviewFormProps) {
  const isVersionReady = (versionId: string) =>
    interviewTypes.some((type) => type.latestVersionId === versionId && isTemplateReady(type));

  const form = useForm({
    defaultValues: {
      interviewTypeVersionId: '',
      firstName: lockedCandidate?.firstName ?? '',
      lastName: lockedCandidate?.lastName ?? '',
      email: lockedCandidate?.email ?? '',
      externalUserId: '',
      externalSource: '',
      channel: 'link' as CreateInterviewFormValues['channel'],
      phoneNumber: '',
      dynamicVariables: {} as Record<string, string>,
    },
    validators: {
      onSubmit: createInterviewFormSchema,
    },
    onSubmit: ({ value }) => {
      // Defensive: not-ready templates are disabled in the dropdown, so this isn't
      // reachable through the UI today — but guard anyway so an unrunnable template
      // can never be submitted (it would 503 on the backend).
      if (value.interviewTypeVersionId && !isVersionReady(value.interviewTypeVersionId)) return;
      onSubmit(value);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      className="space-y-6"
      autoComplete="off"
    >
      <form.Field name="interviewTypeVersionId">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0;
          const selectedNotReady = !!field.state.value && !isVersionReady(field.state.value);
          return (
            <Field data-invalid={hasError || undefined}>
              <FieldLabel htmlFor={field.name}>Template</FieldLabel>
              <FieldContent>
                <Select
                  value={field.state.value}
                  onValueChange={(value) => {
                    field.handleChange(value);
                    // Clear captured values so a variable typed for the previous
                    // template can't survive the switch and get sent as an
                    // undeclared key for the new one (→ a confusing 400).
                    form.setFieldValue('dynamicVariables', {});
                  }}
                  disabled={isPending}
                >
                  <SelectTrigger id={field.name} aria-invalid={hasError}>
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {interviewTypes.map((type) => {
                      const ready = isTemplateReady(type);
                      return (
                        <SelectItem
                          key={type.latestVersionId}
                          value={type.latestVersionId}
                          disabled={!ready}
                          textValue={type.name}
                        >
                          {ready ? (
                            type.name
                          ) : (
                            <span className="flex w-full items-center justify-between gap-3">
                              <span className="flex items-center gap-2">
                                <span
                                  aria-hidden
                                  className="size-1.5 shrink-0 rounded-full bg-warning"
                                />
                                {type.name}
                              </span>
                              <Badge variant="warning">No agent · not ready</Badge>
                            </span>
                          )}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {selectedNotReady && (
                  <p className="text-xs text-warning" data-testid="template-not-ready-warning">
                    This template has no AI agent configured yet, so it can&rsquo;t run interviews
                    or calls. Assign an agent to enable it.
                  </p>
                )}
                <FieldError errors={field.state.meta.errors} />
              </FieldContent>
            </Field>
          );
        }}
      </form.Field>

      {/* Per-template dynamic variables — rendered from the selected template's
          declared definitions; values ride the invite and reach the agent at
          conversation start. */}
      <form.Subscribe selector={(state) => state.values.interviewTypeVersionId}>
        {(selectedVersionId) => {
          const definitions =
            interviewTypes.find((type) => type.latestVersionId === selectedVersionId)
              ?.latestVersionDynamicVariableDefinitions ?? [];
          if (definitions.length === 0) return null;
          return (
            <div className="space-y-4 rounded-lg border border-border/60 p-4">
              <p className="text-[12px] font-medium text-foreground/80">Dynamic variables</p>
              <p className="-mt-2 text-xs text-muted-foreground">
                Passed to the agent at the start of the call.
              </p>
              {definitions.map((def) => {
                const showRequired = def.required && !def.defaultValue;
                return (
                  <form.Field
                    key={def.key}
                    name={`dynamicVariables.${def.key}`}
                    validators={{
                      onSubmit: ({ value }) =>
                        def.required && !def.defaultValue && !(value ?? '').trim()
                          ? { message: `${def.name} is required` }
                          : undefined,
                    }}
                  >
                    {(field) => {
                      const hasError = field.state.meta.errors.length > 0;
                      const value = field.state.value ?? '';
                      return (
                        <Field data-invalid={hasError || undefined}>
                          <FieldLabel htmlFor={field.name}>
                            {def.name}
                            {showRequired ? ' *' : ''}
                          </FieldLabel>
                          <FieldContent>
                            {def.allowedValues.length > 0 ? (
                              <Select
                                value={value || undefined}
                                onValueChange={(v) => field.handleChange(v)}
                                disabled={isPending}
                              >
                                <SelectTrigger id={field.name} aria-invalid={hasError}>
                                  <SelectValue
                                    placeholder={def.defaultValue || 'Choose an option'}
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  {def.allowedValues.map((option) => (
                                    <SelectItem key={option} value={option}>
                                      {option}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : def.type === 'CURRENCY' ? (
                              <div className="flex items-center rounded-md border border-input focus-within:ring-2 focus-within:ring-ring">
                                <span className="px-3 text-sm text-muted-foreground">$</span>
                                <Input
                                  id={field.name}
                                  type="number"
                                  inputMode="decimal"
                                  className="border-0 focus-visible:ring-0"
                                  value={value}
                                  onChange={(e) => field.handleChange(e.target.value)}
                                  placeholder={def.defaultValue ?? ''}
                                  disabled={isPending}
                                  aria-invalid={hasError}
                                />
                              </div>
                            ) : (
                              <Input
                                id={field.name}
                                type={
                                  def.type === 'DATE'
                                    ? 'date'
                                    : def.type === 'NUMBER'
                                      ? 'number'
                                      : 'text'
                                }
                                value={value}
                                onChange={(e) => field.handleChange(e.target.value)}
                                placeholder={def.defaultValue ?? ''}
                                disabled={isPending}
                                aria-invalid={hasError}
                              />
                            )}
                            {def.description ? (
                              <p className="text-xs text-muted-foreground">{def.description}</p>
                            ) : null}
                            <FieldError errors={field.state.meta.errors} />
                          </FieldContent>
                        </Field>
                      );
                    }}
                  </form.Field>
                );
              })}
            </div>
          );
        }}
      </form.Subscribe>

      <div className="grid grid-cols-2 gap-4">
        <form.Field name="firstName">
          {(field) => {
            const hasError = field.state.meta.errors.length > 0;
            return (
              <Field data-invalid={hasError || undefined}>
                <FieldLabel htmlFor={field.name}>First name</FieldLabel>
                <FieldContent>
                  <Input
                    id={field.name}
                    placeholder="Enter first name"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isPending || !!lockedCandidate}
                    aria-invalid={hasError}
                    autoComplete="off"
                  />
                  <FieldError errors={field.state.meta.errors} />
                </FieldContent>
              </Field>
            );
          }}
        </form.Field>

        <form.Field name="lastName">
          {(field) => {
            const hasError = field.state.meta.errors.length > 0;
            return (
              <Field data-invalid={hasError || undefined}>
                <FieldLabel htmlFor={field.name}>Last name</FieldLabel>
                <FieldContent>
                  <Input
                    id={field.name}
                    placeholder="Enter last name"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isPending || !!lockedCandidate}
                    aria-invalid={hasError}
                    autoComplete="off"
                  />
                  <FieldError errors={field.state.meta.errors} />
                </FieldContent>
              </Field>
            );
          }}
        </form.Field>
      </div>

      <form.Field name="email">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0;
          return (
            <Field data-invalid={hasError || undefined}>
              <FieldLabel htmlFor={field.name}>Email</FieldLabel>
              <FieldContent>
                <Input
                  id={field.name}
                  type="email"
                  placeholder="Enter email address"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={isPending || !!lockedCandidate}
                  aria-invalid={hasError}
                  autoComplete="off"
                />
                <FieldError errors={field.state.meta.errors} />
              </FieldContent>
            </Field>
          );
        }}
      </form.Field>

      <form.Field name="channel">
        {(field) => (
          <Field>
            <FieldLabel htmlFor={field.name}>How to send</FieldLabel>
            <FieldContent>
              <RadioGroup
                id={field.name}
                value={field.state.value}
                onValueChange={(value) =>
                  field.handleChange(value as CreateInterviewFormValues['channel'])
                }
                aria-label="How to send the interview"
                className="grid grid-cols-2 gap-2.5"
              >
                <DeliveryOption
                  value="link"
                  selected={field.state.value === 'link'}
                  icon={<Link2 className="h-4 w-4" strokeWidth={1.6} />}
                  title="Share a link"
                  description="Create an invite link to send yourself."
                  disabled={isPending}
                />
                <DeliveryOption
                  value="call"
                  selected={field.state.value === 'call'}
                  icon={<Phone className="h-4 w-4" strokeWidth={1.6} />}
                  title="Call now"
                  description="Dial the candidate right away."
                  disabled={isPending}
                />
              </RadioGroup>
            </FieldContent>
          </Field>
        )}
      </form.Field>

      <form.Subscribe selector={(state) => state.values.channel}>
        {(channel) =>
          channel === 'call' ? (
            <form.Field name="phoneNumber">
              {(field) => {
                const hasError = field.state.meta.errors.length > 0;
                return (
                  <Field data-invalid={hasError || undefined}>
                    <FieldLabel htmlFor={field.name}>Phone number</FieldLabel>
                    <FieldContent>
                      <PhoneInput
                        id={field.name}
                        placeholder="(555) 123-4567"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(value) => field.handleChange(value)}
                        disabled={isPending}
                        aria-invalid={hasError}
                        autoComplete="off"
                      />
                      <p className="text-xs text-muted-foreground">US phone numbers only.</p>
                      <FieldError errors={field.state.meta.errors} />
                    </FieldContent>
                  </Field>
                );
              }}
            </form.Field>
          ) : null
        }
      </form.Subscribe>

      <Collapsible className="group/advanced border-t border-border/60 pt-4">
        <CollapsibleTrigger
          className="flex w-full items-center justify-between gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:text-foreground disabled:opacity-60"
          disabled={isPending}
        >
          <span className="flex items-center gap-2">
            Advanced options
            <span className="text-xs font-normal text-muted-foreground/70">External tracking</span>
          </span>
          <ChevronDown
            aria-hidden
            className="size-4 shrink-0 transition-transform duration-200 group-data-[state=open]/advanced:rotate-180"
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
          <div className="space-y-4 pt-4">
            <form.Field name="externalUserId">
              {(field) => {
                const hasError = field.state.meta.errors.length > 0;
                return (
                  <Field data-invalid={hasError || undefined}>
                    <FieldLabel htmlFor={field.name}>External user ID</FieldLabel>
                    <FieldContent>
                      <Input
                        id={field.name}
                        placeholder="usr_abc123"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        disabled={isPending}
                        aria-invalid={hasError}
                        autoComplete="off"
                      />
                      <FieldError errors={field.state.meta.errors} />
                    </FieldContent>
                  </Field>
                );
              }}
            </form.Field>

            <form.Field name="externalSource">
              {(field) => {
                const hasError = field.state.meta.errors.length > 0;
                return (
                  <Field data-invalid={hasError || undefined}>
                    <FieldLabel htmlFor={field.name}>External source</FieldLabel>
                    <FieldContent>
                      <Input
                        id={field.name}
                        placeholder="your-system"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        disabled={isPending}
                        aria-invalid={hasError}
                        autoComplete="off"
                      />
                      <FieldError errors={field.state.meta.errors} />
                    </FieldContent>
                  </Field>
                );
              }}
            </form.Field>
          </div>
        </CollapsibleContent>
      </Collapsible>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          shape="pill"
          size="sm"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </Button>
        <form.Subscribe
          selector={(state) => ({
            channel: state.values.channel,
            typeVersionId: state.values.interviewTypeVersionId,
          })}
        >
          {({ channel, typeVersionId }) => (
            <Button
              type="submit"
              variant="tinted"
              shape="pill"
              size="sm"
              disabled={isPending || (!!typeVersionId && !isVersionReady(typeVersionId))}
              className="gap-1.5"
            >
              {channel === 'call' ? (
                <>
                  <Phone className="h-3.5 w-3.5" strokeWidth={1.6} aria-hidden />
                  Create &amp; call
                </>
              ) : (
                'Create interview'
              )}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </form>
  );
}

interface DeliveryOptionProps {
  value: CreateInterviewFormValues['channel'];
  selected: boolean;
  icon: React.ReactNode;
  title: string;
  description: string;
  disabled: boolean;
}

function DeliveryOption({
  value,
  selected,
  icon,
  title,
  description,
  disabled,
}: DeliveryOptionProps) {
  const id = `delivery-${value}`;
  return (
    <label
      htmlFor={id}
      className={cn(
        'group flex cursor-pointer flex-col gap-1 rounded-xl border px-3.5 py-3 transition-colors',
        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-background',
        disabled && 'cursor-not-allowed opacity-60',
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary'
          : 'border-border hover:bg-accent'
      )}
    >
      <span className="flex items-center justify-between">
        <span
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full transition-colors',
            selected ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
          )}
        >
          {icon}
        </span>
        <RadioGroupItem id={id} value={value} disabled={disabled} className="h-4 w-4" />
      </span>
      <span className="mt-1 text-sm font-medium text-foreground">{title}</span>
      <span className="text-xs text-muted-foreground">{description}</span>
    </label>
  );
}

export { CreateInterviewForm };
